import { Injectable, inject } from '@angular/core';
import { API as AWS_API } from 'aws-amplify';
import { CONST_AWS_API } from 'src/app/core/constants';
import { environment } from 'src/environments/environment';
import { CommanSubscribeService } from './comman-subscribe.service';

export interface PublicProfile {
  nickname: string;
  profileUrl?: string;
}

const FAN_PREFIX = 'fan_';
const MAX_PER_REQUEST = 100;

/**
 * Display name + photo for community members (consumer-service getPublicProfiles,
 * which returns only those two fields). Keyed by PubNub user id (fan_<id>).
 * Each id is requested at most once per session; failures leave it unnamed.
 */
@Injectable({ providedIn: 'root' })
export class CommunityProfilesService {
  private readonly subscribeService = inject(CommanSubscribeService);
  private readonly profiles = new Map<string, PublicProfile>();
  private readonly requested = new Set<string>();

  get(userId: string): PublicProfile | undefined {
    return this.profiles.get(userId);
  }

  /** Fetches names for ids not seen before; resolves true when new names arrived. */
  async load(userIds: string[]): Promise<boolean> {
    const token = this.subscribeService.userInfo?.access_token;
    const ids = userIds.filter(id => id?.startsWith(FAN_PREFIX) && !this.requested.has(id));
    if (!token || !ids.length) return false;
    ids.forEach(id => this.requested.add(id));

    let added = false;
    for (let i = 0; i < ids.length; i += MAX_PER_REQUEST) {
      const batch = ids.slice(i, i + MAX_PER_REQUEST).map(id => id.slice(FAN_PREFIX.length));
      try {
        const res: any = await AWS_API.post(environment.AWS_Cognito.apiName, CONST_AWS_API.GET_PUBLIC_PROFILES, {
          headers: { 'x-api-key': environment.AWS_Cognito.X_Api_Key, Authorization: `Bearer ${token}` },
          body: { user_ids: batch },
        });
        (res?.profiles || []).forEach((p: any) => {
          if (!p?.display_name) return;
          this.profiles.set(FAN_PREFIX + p.id, { nickname: p.display_name, profileUrl: p.profile_pic || undefined });
          added = true;
        });
      } catch {
        // names are optional: members stay listed as "Member"
      }
    }
    return added;
  }
}
